/**
 *  @swagger
 *  definitions:
 *    RegattaEntryCrewmemberParams:
 *      type: object
 *      properties:
 *        RegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        SortId:
 *          description: Sort ID of the RegattaEntryCrewmember
 *          type: integer
 *        Name:
 *          description: Name of the RegattaEntryCrewmember
 *          type: string
 *        Birthdate:
 *          description: Birthdate of the RegattaEntryCrewmember
 *          type: string
 *          format: date
 *        Email:
 *          description: Email of the RegattaEntryCrewmember
 *          type: string
 *        Phone:
 *          description: Phone of the RegattaEntryCrewmember
 *          type: string
 *        Gender:
 *          description: Gender of the RegattaEntryCrewmember
 *          $ref: '#/components/enums/RegattaEntryCrewmemberGender'
 *        Tshirt:
 *          description: Tshirt of the RegattaEntryCrewmember
 *          $ref: '#/components/enums/RegattaEntryCrewmemberTshirt'
 *        Qualification:
 *          description: Qualification of the RegattaEntryCrewmember
 *          type: string
 *        Weight:
 *          description: Weight of the RegattaEntryCrewmember
 *          type: integer
 *        Membership:
 *          description: Membership of the RegattaEntryCrewmember
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCrewmemberCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryCrewmemberParams'
 *        - type: object
 *          required:
 *            - RegattaEntryId
 *            - SortId
 *            - Name
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCrewmemberQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaEntryCrewmember
 *              type: string
 *        - $ref: '#/definitions/RegattaEntryCrewmemberParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCrewmemberUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryCrewmemberQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCrewmember:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryCrewmemberCreateParams'
 *        - $ref: '#/definitions/RegattaEntryCrewmemberUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaEntryCrewmember:
 *        allOf:
 *          - $ref: '#/definitions/RegattaEntryCrewmember'
 *          - type: object
 *            properties:
 *              RegattaEntry:
 *                description: RegattaEntry of the RegattaEntryCrewmember
 *                $ref: '#/components/schemas/RegattaEntry'
 *              Crewmember:
 *                description: Crewmember of the RegattaEntryCrewmember
 *                $ref: '#/components/schemas/Crewmember'
 */

// Enums
import {
  RegattaEntryCrewmemberGender,
  RegattaEntryCrewmemberTshirt,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaEntryId: string;
};

export type $Params = {
  Birthdate: null | string;
  Email: null | string;
  Gender: null | RegattaEntryCrewmemberGender;
  Membership: null | string;
  Name: string;
  Phone: null | string;
  Qualification: null | string;
  SortId: number;
  Tshirt: null | RegattaEntryCrewmemberTshirt;
  Weight: null | number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Crewmember?: $Model<'Crewmember'>;
  RegattaEntry?: $Model<'RegattaEntry'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Birthdate: {
    allowNull: true,
    type: Sequelize.DATEONLY,
    validate: {
      notEmpty: true,
    },
  },
  Email: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  Gender: {
    allowNull: true,
    type: Sequelize.ENUM(...Object.values(RegattaEntryCrewmemberGender)),
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Membership: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Phone: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Qualification: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  SortId: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Tshirt: {
    allowNull: true,
    type: Sequelize.ENUM(...Object.values(RegattaEntryCrewmemberTshirt)),
    validate: {
      notEmpty: true,
    },
  },
  Weight: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaEntryCrewmember'> => {
  const RegattaEntryCrewmember = stack.define(
    'RegattaEntryCrewmember',
    fields(Sequelize),
    {
      tableName: 'RegattaEntryCrewmembers',
    },
  );

  RegattaEntryCrewmember.associate = (models: $ModelClasses) => {
    models.RegattaEntryCrewmember.belongsTo(
      models.RegattaEntry,
      {
        as: 'RegattaEntry',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaEntryCrewmember.hasOne(
      models.Crewmember,
      {
        as: 'Crewmember',
        constraints: false,
        foreignKey: 'Email',
        sourceKey: 'Email',
      },
    );
  };

  RegattaEntryCrewmember.addScope(
    'defaultScope',
    {
      order: [['SortId', 'ASC']],
    },
    {
      override: true,
    },
  );

  return RegattaEntryCrewmember;
});
